// What the builder draws ahead of the engine. Each entry is authorable and saves, and every use of
// it blocks Activate until its backend ticket passes QA, at which point the entry is deleted here
// (appointment confirmation plan, Phase 2). One list, so "what is still waiting on the engine" has
// one answer.
import { fieldLabelByKey } from './dataContext'
import type { WorkflowGap } from './serialize'
import type { WorkflowNode } from 'app/types/workflows'

/** Fields the engine cannot read or render yet. Empty since the appointment confirmation epic
 *  (TRK-6073) landed on 2026-10-29. */
const PENDING_APPOINTMENT_FIELDS: ReadonlySet<string> = new Set<string>([])

const ACTIVITY_TOKEN = /\{\{\s*activity\.([A-Za-z]+)/g

const TEXT_KEYS = [`message`, `body`, `title`, `description`, `brief`, `whisper`] as const

function pendingFieldsIn(node: WorkflowNode): string[] {
  const data = node.data as Record<string, unknown>
  // An Edit step's values are typed text too, so a token there renders blank the same way.
  const editValues = Array.isArray(data.fields) ? (data.fields as { value?: unknown }[]).map((row) => row.value) : []
  const texts = [...TEXT_KEYS.map((key) => data[key]), ...editValues].filter(
    (value): value is string => typeof value === `string`,
  )
  const tokens = texts.flatMap((text) => [...text.matchAll(ACTIVITY_TOKEN)].map((match) => match[1]))
  const conditions = Array.isArray(data.conditions) ? (data.conditions as { field?: string }[]) : []
  const fields = conditions.flatMap((condition) => (condition.field ? [condition.field] : []))
  return [...new Set([...tokens, ...fields])].filter((key) => PENDING_APPOINTMENT_FIELDS.has(key))
}

/** One blocking gap per node that uses something the engine cannot run yet. */
export function pendingEngineGaps(nodes: WorkflowNode[]): WorkflowGap[] {
  return nodes.flatMap((node): WorkflowGap[] => {
    const reasons: string[] = []
    const fields = pendingFieldsIn(node)
    if (fields.length) {
      const names = fields.map((key) => fieldLabelByKey(key) ?? key).join(`, `)
      reasons.push(`${names}: the workflow engine can't use ${fields.length === 1 ? `this` : `these`} yet (TRK-6077).`)
    }
    if (!reasons.length) return []
    return [{ nodeId: node.id, code: `unsupported_node`, message: `${reasons.join(` `)} Flagged for backend.` }]
  })
}
